"use client";

import { useState } from 'react';
import { useRegionalFormatters } from '@/lib/use-regional-formatters';
import { Button } from '@/components/ui/button';
import { Download, FileText, Check, Shield } from 'lucide-react';

interface WhitepaperDownloadProps {
  config: {
    headline?: string;
    subheadline?: string;
    resourceSlug?: string;
    benefits?: string[];
    fileSize?: string;
    pages?: string;
    format?: string;
  };
  locale: string;
}

export function WhitepaperDownloadSection({ config, locale }: WhitepaperDownloadProps) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);

    try {
      // TODO: Connect to backend API for download tracking
      const response = await fetch(`/api/resources/download/${config.resourceSlug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language_code: locale }),
      });

      if (response.ok) {
        const data = await response.json();
        // Trigger download
        window.open(data.downloadUrl || '/downloads/nextpal-security-whitepaper.pdf', '_blank');
      }
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: direct download
      window.open('/downloads/nextpal-security-whitepaper.pdf', '_blank');
    } finally {
      setTimeout(() => setDownloading(false), 1000);
    }
  };

  return (
    <section className="relative py-24 px-4 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-950" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.1),transparent_50%)]" />

      <div className="relative z-10 container mx-auto max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <div>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-600 dark:text-blue-400 font-semibold mb-6">
              <Shield className="w-4 h-4" />
              Free Download
            </div>

            {/* Headline */}
            <h2 className="text-4xl md:text-5xl font-black mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              {config.headline || 'Download Our Security Whitepaper'}
            </h2>

            {/* Subheadline */}
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
              {config.subheadline || 'Comprehensive guide to PostgreSQL backup security: encryption standards, compliance frameworks, and enterprise best practices.'}
            </p>

            {/* Benefits */}
            {config.benefits && config.benefits.length > 0 && (
              <div className="space-y-3 mb-8">
                {config.benefits.map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mt-0.5">
                      <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Download Button */}
            <Button
              size="lg"
              onClick={handleDownload}
              disabled={downloading}
              className="h-14 px-8 text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold shadow-lg hover:shadow-xl transition-all group"
            >
              {downloading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Preparing Download...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Download className="w-5 h-5 group-hover:translate-y-0.5 transition-transform" />
                  Download Whitepaper
                </span>
              )}
            </Button>

            {/* File Info */}
            <div className="flex items-center gap-6 mt-6 text-sm text-gray-600 dark:text-gray-400">
              {config.format && (
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {config.format}
                </div>
              )}
              {config.fileSize && <span>• {config.fileSize}</span>}
              {config.pages && <span>• {config.pages}</span>}
            </div>
          </div>

          {/* Right: Visual */}
          <div className="relative">
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl blur-3xl opacity-20" />
            
            {/* Document Preview */}
            <div className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-800 p-8 transform hover:scale-105 transition-transform duration-500">
              {/* Document Icon */}
              <div className="w-full aspect-[3/4] bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 rounded-2xl flex items-center justify-center mb-6 border-2 border-dashed border-blue-300 dark:border-blue-700">
                <div className="text-center">
                  <FileText className="w-24 h-24 mx-auto mb-4 text-blue-600 dark:text-blue-400" />
                  <div className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                    Security Whitepaper
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {config.pages || '24 pages'} • {config.format || 'PDF'}
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-black text-blue-600 dark:text-blue-400">24</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Pages</div>
                </div>
                <div>
                  <div className="text-2xl font-black text-purple-600 dark:text-purple-400">6</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Chapters</div>
                </div>
                <div>
                  <div className="text-2xl font-black text-green-600 dark:text-green-400">2.4</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">MB</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
